// =====================================================================
// src/lib/relatorios.js — a página Relatórios do MdM, a parte que é do
// Financeiro, sem DOM
//   (MdM `renderRelatorios` 20517 · `_calcRelatorio` 24074 ·
//    `_calcResumoFlats` resumo-flats.js · `_calcCustoOperacional`
//    custo-operacional.js · `gerarRankingFlats` 22819 ·
//    `_renderResumoRelatorioHtml` 23568 · `_renderRelatorioChart` 24031)
//
// Quatro cards, cada um com De/Até:
//   rel-reservas  — todas as reservas do período (inclusive canceladas),
//                   Total ou Por Flat (o seletor por proprietário); Por
//                   Flat traz o resumo: cards de KPI, custo operacional e
//                   o gráfico ocupação × rentabilidade
//   ranking-flats — pódio + tabela por receita das reservas (uso interno)
//   condominio    — lançamentos de condomínio + a linha "(Previsto — não
//                   lançado no período)" dos flats com valor cadastrado
//   funcionaria   — lançamentos `funcionarios`/`salario` (decisão do Hugo
//                   "3A", 22/09: exatamente como a Monteiro tem hoje)
//
// Divergências de domínio, todas comentadas no lugar:
//   · a taxa/rateio vem do ACORDO (0049), não do nome do proprietário —
//     próprio / cota fixa / percentual; sem acordo, sem taxa e avisado;
//   · o IPTU previsto vem de `iptu_da_unidade` (0053), o condomínio de
//     `condominio_da_unidade` (0049) — no MdM são campos do cadastro do flat;
//   · "Até" do Condomínio é o ÚLTIMO DIA REAL do mês (o MdM manda '-31' e
//     deixa o banco normalizar; um <input type=date> recusa 30/02);
//   · status `noshow` (o SGH não tem `pendente`); frigobar, loja, site,
//     WhatsApp, LGPD, intercorrências, acessos e tempo ficam fora (itens 6/7
//     e módulos próprios deste app).
// =====================================================================
import { noites } from './reservas.js';
import { acordoDe, repasse, iptuPrevisto } from './financeiro.js';
export { iptuPrevisto };
import { GERAL, SEM_PROPRIETARIO, rotuloDaTaxa } from './relatorio_proprietario.js';

const num = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const porNumero = (a, b) => (parseInt(a.numero, 10) || 0) - (parseInt(b.numero, 10) || 0);
const moedaPadrao = v => 'R$ ' + num(v).toFixed(2).replace('.', ',');

// os cards (20524), na ordem do MdM; `porFlat` = os botões Total / Por Flat
export const CARDS = Object.freeze([
  { id: 'rel-reservas', icone: 'ic-relatorios', titulo: 'Relatório de Reservas', cor: '#0f766e', porFlat: true,
    desc: 'Todas as reservas do período com detalhes: flat, hóspede, plataforma, noites, valor, diária média e status.' },
  { id: 'ranking-flats', icone: 'ic-trophy', titulo: 'Ranking de Flats', cor: '#f59e0b', ranking: true,
    desc: 'Rankeamento dos flats por receita das reservas no período (uso interno — não compartilhado com demais proprietários).' },
  { id: 'condominio', icone: 'ic-flats', titulo: 'Condomínio', cor: '#7c3aed', porFlat: true, ateFimDoMes: true,
    desc: 'Lançamentos de condomínio dos flats por período.' },
  { id: 'funcionaria', icone: 'ic-funcionarios', titulo: 'Despesa Funcionária', cor: '#db2777',
    desc: 'Salário, vale alimentação, transporte, INSS e impostos da funcionária.' },
]);

export function ultimoDiaDoMes(iso) {
  const [a, m] = iso.split('-').map(Number);
  return `${iso.slice(0, 7)}-${String(new Date(Date.UTC(a, m, 0)).getUTCDate()).padStart(2, '0')}`;
}
// De = primeiro dia do mês corrente; Até = hoje (Condomínio: o fim do mês) — 20518
export function periodoPadrao(card, hoje) {
  return { de: hoje.slice(0, 7) + '-01', ate: card && card.ateFimDoMes ? ultimoDiaDoMes(hoje) : hoje };
}
// as duas recusas do MdM (23398)
export function validarPeriodo(de, ate) {
  if (!de || !ate) return 'Selecione o período.';
  if (de > ate) return 'A data inicial deve ser anterior à final.';
  return null;
}
export const diasDoPeriodo = (de, ate) => Math.max(1, noites(de, ate) + 1);
const dentro = (d, de, ate) => !!d && d >= de && d <= ate;

// ── `_calcRelatorio` (24074): { titulo, headers, rows, totais } ───────
// dados = { unidades, reservas, valorDe, lancamentos, condominios }
// fmt = { moeda, dataBR, hospedeDe }
export const STATUS = Object.freeze({ confirmada: '✅ Confirmada', concluida: '🚩 Concluída', cancelada: '❌ Cancelada', noshow: '🚫 No-show' });
export function relatorio(tipo, dados, de, ate, flatIds, fmt) {
  const moeda = fmt.moeda || moedaPadrao, dataBR = fmt.dataBR || (d => d), hospedeDe = fmt.hospedeDe || (() => null);
  const unidades = dados.unidades || [];
  const filtro = Array.isArray(flatIds) && flatIds.length > 0;
  const set = new Set(filtro ? flatIds : []);
  const passa = fid => !filtro || (fid && set.has(fid));
  const flatDe = id => unidades.find(u => u.id === id);
  const nomeDoFlat = id => { const u = flatDe(id); return u ? 'Flat ' + u.numero : null; };

  if (tipo === 'rel-reservas') {
    const todas = (dados.reservas || []).filter(r => dentro(r.check_in, de, ate) && passa(r.unidade_id)).sort((a, b) => a.check_in.localeCompare(b.check_in));
    const valorDe = dados.valorDe || (() => null);
    const rows = todas.map(r => {
      const n = noites(r.check_in, r.check_out), v = num(valorDe(r)), diaria = n > 0 ? v / n : 0;
      return [dataBR(r.check_in), dataBR(r.check_out), nomeDoFlat(r.unidade_id) || '-', hospedeDe(r) || '-', r.plataforma || '-', n + ' nts', moeda(v), diaria > 0 ? moeda(diaria) : '—', STATUS[r.situacao] || r.situacao || '-'];
    });
    const ativas = todas.filter(r => r.situacao !== 'cancelada');
    const totValor = ativas.reduce((s, r) => s + num(valorDe(r)), 0), totNoites = ativas.reduce((s, r) => s + noites(r.check_in, r.check_out), 0);
    const media = totNoites > 0 ? totValor / totNoites : 0;
    return { titulo: 'Relatório de Reservas', headers: ['Check-in', 'Check-out', 'Flat', 'Hóspede', 'Plataforma', 'Noites', 'Valor', 'Diária', 'Status'], rows,
      totais: [`TOTAL (${ativas.length} ativas / ${todas.length - ativas.length} canc.)`, '', '', '', '', totNoites + ' nts', moeda(totValor), media > 0 ? 'Média: ' + moeda(media) : '—', ''] };
  }
  if (tipo === 'condominio') {
    const lanc = (dados.lancamentos || []).filter(l => l.categoria === 'condominio' && dentro(l.data, de, ate) && (filtro ? l.unidade_id && set.has(l.unidade_id) : true)).sort((a, b) => a.data.localeCompare(b.data));
    const rows = lanc.map(l => [dataBR(l.data), nomeDoFlat(l.unidade_id) || 'Geral', l.descricao || '-', l.tipo === 'receita' ? moeda(l.valor) : '-', l.tipo === 'despesa' ? moeda(l.valor) : '-']);
    const comLancamento = new Set(lanc.filter(l => l.tipo === 'despesa' && l.unidade_id).map(l => l.unidade_id));
    const cadastro = new Map((dados.condominios || []).map(c => [c.unidade_id, num(c.valor)]));
    const previstos = unidades.filter(u => cadastro.get(u.id) > 0 && !comLancamento.has(u.id) && passa(u.id)).sort(porNumero);
    for (const u of previstos) rows.push(['-', 'Flat ' + u.numero, '(Previsto — não lançado no período)', '-', moeda(cadastro.get(u.id)) + ' *']);
    const soma = t => lanc.filter(l => l.tipo === t).reduce((s, l) => s + num(l.valor), 0);
    const totPrev = previstos.reduce((s, u) => s + cadastro.get(u.id), 0);
    return { titulo: 'Condomínio', headers: ['Data', 'Flat', 'Descrição', 'Receita', 'Despesa'], rows,
      totais: ['TOTAL (lançado)', '', '', moeda(soma('receita')), moeda(soma('despesa')) + (totPrev > 0 ? ' | Previsto: ' + moeda(totPrev) + '*' : '')] };
  }
  if (tipo === 'funcionaria') {
    const lanc = (dados.lancamentos || []).filter(l => (l.categoria === 'funcionarios' || l.categoria === 'salario') && dentro(l.data, de, ate)).sort((a, b) => a.data.localeCompare(b.data));
    const rows = lanc.map(l => { const p = String(l.descricao || '').split('|'); return [dataBR(l.data), p[0].trim(), p.slice(1).join('|').trim() || '-', moeda(l.valor)]; });
    return { titulo: 'Despesa Funcionária', headers: ['Data', 'Tipo', 'Observação', 'Valor'], rows, totais: ['TOTAL', '', '', moeda(lanc.reduce((s, l) => s + num(l.valor), 0))] };
  }
  return null;
}

// ── `_calcResumoFlats` (resumo-flats.js): 16 campos por flat ──────────
// dados = { unidades, reservas, valorDe, lancamentos, acordos }
// Receita de reserva PRO-RATA POR NOITE (30/04/2026); frigobar fora; a
// taxa pelo acordo: próprio 0 · cota fixa valor × meses ÷ flats do
// proprietário (só para exibir — o total é fixo) · percentual × receita.
export function resumoFlats(dados, flatIds, de, ate, moeda = moedaPadrao) {
  const unidades = dados.unidades || [], acordos = dados.acordos || [];
  const valorDe = dados.valorDe || (() => null);
  const set = new Set(flatIds);
  const dias = diasDoPeriodo(de, ate), meses = dias / 30.0;
  const nomeDe = u => (u && u.proprietarios && u.proprietarios.nome) || (u && u.proprietario_id ? 'Proprietário' : 'Sem proprietário');
  const porFlat = new Map();
  for (const id of flatIds) {
    const u = unidades.find(x => x.id === id);
    porFlat.set(id, { flatId: id, numero: u ? u.numero : '?', proprietarioId: u ? u.proprietario_id || null : null, proprietario: nomeDe(u), noites: 0, receita: 0, receitaDiarias: 0, despesa: 0 });
  }
  const fimEx = somarDia(ate, 1);
  for (const r of dados.reservas || []) {
    if (!r.unidade_id || !set.has(r.unidade_id) || r.situacao === 'cancelada' || !r.check_in || !r.check_out) continue;
    const ini = r.check_in > de ? r.check_in : de, fim = r.check_out < fimEx ? r.check_out : fimEx;
    if (ini >= fim) continue;
    const nDentro = noites(ini, fim); if (nDentro <= 0) continue;
    const prop = num(valorDe(r)) * (nDentro / Math.max(1, noites(r.check_in, r.check_out)));
    const x = porFlat.get(r.unidade_id); x.noites += nDentro; x.receita += prop; x.receitaDiarias += prop;
  }
  for (const l of dados.lancamentos || []) {
    if (!l.unidade_id || !set.has(l.unidade_id) || !dentro(l.data, de, ate) || l.categoria === 'frigobar') continue;
    const x = porFlat.get(l.unidade_id);
    if (l.tipo === 'despesa') x.despesa += num(l.valor);
    if (l.tipo === 'receita' && l.categoria !== 'diarias' && l.categoria !== 'aluguel') x.receita += num(l.valor);
  }
  for (const x of porFlat.values()) {
    const a = x.proprietarioId ? acordoDe(acordos, x.proprietarioId) : null;
    if (!a) { x.taxaAdm = 0; x.regraTaxa = x.proprietarioId ? 'Sem acordo cadastrado — sem taxa' : 'Sem proprietário — sem taxa'; }
    else if (a.tipo === 'proprio') { x.taxaAdm = 0; x.regraTaxa = 'Próprio — sem taxa'; }
    else if (a.tipo === 'cota_fixa') {
      const n = unidades.filter(u => u.proprietario_id === x.proprietarioId).length || 1;
      x.taxaAdm = repasse(a, 0, meses) / n; x.regraTaxa = `${moeda(a.valor)}/mês fixo ÷ ${n} flats`;
    } else { x.taxaAdm = repasse(a, x.receita, 1); x.regraTaxa = `${num(a.valor)}% × receita`; }
    x.acordo = a;
    x.rentabilidadeBruta = x.receita - x.despesa;
    x.rentabilidadeLiquida = x.receita - x.despesa - x.taxaAdm;
    x.ocupacao = (x.noites / dias) * 100;
    x.diariaMedia = x.noites > 0 ? x.receitaDiarias / x.noites : 0;
  }
  const linhas = [...porFlat.values()].sort(porNumero);
  const soma = c => linhas.reduce((s, x) => s + x[c], 0);
  const totalNoitesDisp = flatIds.length * dias, totalNoitesOcup = soma('noites');
  const totalReceita = soma('receita'), totalReceitaDiarias = soma('receitaDiarias'), totalDespesa = soma('despesa'), totalTaxaAdm = soma('taxaAdm');
  return { linhas, diasPeriodo: dias, mesesPeriodo: meses, totalNoitesDisp, totalNoitesOcup, totalReceita, totalReceitaDiarias, totalDespesa, totalTaxaAdm,
    ocupacao: totalNoitesDisp > 0 ? (totalNoitesOcup / totalNoitesDisp) * 100 : 0,
    diariaMedia: totalNoitesOcup > 0 ? totalReceitaDiarias / totalNoitesOcup : 0,
    rentabilidadeBruta: totalReceita - totalDespesa, rentabilidadeLiquida: totalReceita - totalDespesa - totalTaxaAdm };
}
function somarDia(iso, n) { const [a, m, d] = iso.split('-').map(Number); return new Date(Date.UTC(a, m - 1, d + n)).toISOString().slice(0, 10); }

// ── `_calcCustoOperacional` (custo-operacional.js) ────────────────────
// dados = { unidades, lancamentos, despesas, acordos, condominios, iptus }
// prop = proprietario_id · GERAL · SEM_PROPRIETARIO. O ramo vem do ACORDO:
//   próprio     → Condomínio + IPTU + Variáveis [+ Outras]
//   cota fixa   → Condomínio + <cota rateada> + IPTU + Variáveis [+ Outras]
//   percentual  → Variáveis [+ Outras] + Taxa de Administração (% × receita do flat)
//   sem acordo  → Variáveis [+ Outras], sem taxa (avisado)
//   GERAL       → Condomínio + IPTU + Variáveis + cada cota fixa rateada [+ Outras]
// Devolve { total, itens, prop, porFlat }; invariante: Σ porFlat.total = total.
// Loja, frigobar e funcionários NÃO entram (07/09/2026: são da holding).
export function custoOperacional(dados, prop, flatIds, de, ate, receitaPorFlat, moeda = moedaPadrao) {
  const unidades = dados.unidades || [], acordos = dados.acordos || [];
  const set = new Set(flatIds || []);
  const dias = diasDoPeriodo(de, ate), meses = dias / 30, nMeses = Math.max(1, Math.round(meses));
  const sel = unidades.filter(u => set.has(u.id));
  const porFlat = {};
  for (const u of sel) porFlat[u.id] = { flatId: u.id, numero: u.numero, proprietario: (u.proprietarios && u.proprietarios.nome) || '', condominio: 0, iptu: 0, variaveis: 0, avulsos: 0, compartilhado: 0, total: 0 };
  const add = (fid, campo, v) => { if (porFlat[fid] && v) porFlat[fid][campo] += v; };
  const lancFlat = (fid, cats) => (dados.lancamentos || []).filter(l => l.tipo === 'despesa' && l.unidade_id === fid && dentro(l.data, de, ate) && cats.includes(l.categoria)).reduce((s, l) => s + num(l.valor), 0);
  const variaveisFlat = fid => (dados.despesas || []).filter(d => d.unidade_id === fid && dentro(d.data, de, ate)).reduce((s, d) => s + num(d.valor), 0);
  const avulsosFlat = fid => (dados.lancamentos || []).filter(l => l.tipo === 'despesa' && l.unidade_id === fid && dentro(l.data, de, ate) && !['condominio', 'iptu', 'loja', 'funcionarios', 'salario', 'frigobar'].includes(l.categoria || '')).reduce((s, l) => s + num(l.valor), 0);
  const condominioDe = new Map((dados.condominios || []).map(c => [c.unidade_id, num(c.valor)]));
  const iptuDe = new Map((dados.iptus || []).map(i => [i.unidade_id, i]));
  // rateio da cota fixa pelo grupo do proprietário: cada flat SELECIONADO
  // recebe valor ÷ TOTAL de flats dele — o grupo inteiro devolve o cheio
  const distribuir = (proprietarioId, valorCheio) => {
    const todos = unidades.filter(u => u.proprietario_id === proprietarioId), s = todos.filter(u => set.has(u.id));
    if (!todos.length || !s.length || !valorCheio) return { valor: 0, n: todos.length, nSel: s.length };
    const porUnidade = valorCheio / todos.length;
    for (const u of s) add(u.id, 'compartilhado', porUnidade);
    return { valor: porUnidade * s.length, n: todos.length, nSel: s.length };
  };
  // condomínio e IPTU: "lançado × previsão" é decisão GLOBAL (qualquer lançamento no período → lançado)
  const aplicarCondIptu = flats => {
    const usaPrevCond = !(flats.reduce((s, f) => s + lancFlat(f.id, ['condominio']), 0) > 0);
    for (const f of flats) add(f.id, 'condominio', usaPrevCond ? (condominioDe.get(f.id) || 0) * nMeses : lancFlat(f.id, ['condominio']));
    const usaPrevIptu = !(flats.reduce((s, f) => s + lancFlat(f.id, ['iptu']), 0) > 0);
    for (const f of flats) add(f.id, 'iptu', usaPrevIptu ? iptuPrevisto(iptuDe.get(f.id), de, ate) : lancFlat(f.id, ['iptu']));
    return { condPrev: usaPrevCond, iptuPrev: usaPrevIptu };
  };
  const somaCampo = (flats, campo) => flats.reduce((s, f) => s + (porFlat[f.id] ? porFlat[f.id][campo] : 0), 0);
  const status = (prev, v) => (!prev ? (v > 0 ? 'lancado' : 'zero') : (v > 0 ? 'previsao' : 'zero'));
  const itens = [];
  const itemCond = flags => { const v = somaCampo(sel, 'condominio'); itens.push({ label: 'Condomínio', valor: v, sub: !flags.condPrev ? 'soma dos condomínios mensais' : `previsão: valor cadastrado × ${nMeses} mês(es)`, status: status(flags.condPrev, v) }); };
  const itemIptu = flags => { const v = somaCampo(sel, 'iptu'); itens.push({ label: 'IPTU', valor: v, sub: !flags.iptuPrev ? 'parcelas pagas no período' : (v > 0 ? 'previsão: parcelas que caem no período' : 'sem cadastro de IPTU'), status: status(flags.iptuPrev, v) }); };
  const itemVariaveis = txt => { for (const f of sel) add(f.id, 'variaveis', variaveisFlat(f.id)); const v = somaCampo(sel, 'variaveis'); itens.push({ label: 'Despesas Variáveis', valor: v, sub: v > 0 ? txt : 'nenhuma despesa variável no período', status: v > 0 ? 'lancado' : 'zero' }); };
  const itemOutras = () => { for (const f of sel) add(f.id, 'avulsos', avulsosFlat(f.id)); const v = somaCampo(sel, 'avulsos'); if (v > 0) itens.push({ label: 'Outras Despesas', valor: v, sub: 'lançamentos avulsos do período', status: 'lancado' }); };
  const itemCota = (proprietarioId, a, nome) => {
    const r = distribuir(proprietarioId, repasse(a, 0, meses));
    itens.push({ label: nome ? `${rotuloDaTaxa(a)} (${nome})` : rotuloDaTaxa(a), valor: r.valor, sub: `cota fixa ${moeda(a.valor)}/mês × ${meses.toFixed(2)} · ${r.nSel}/${r.n} flats`, status: r.valor > 0 ? 'lancado' : 'zero' });
  };

  const acordo = prop === GERAL || prop === SEM_PROPRIETARIO ? null : acordoDe(acordos, prop);
  const tipo = prop === GERAL ? 'geral' : (acordo ? acordo.tipo : 'sem_acordo');
  if (tipo === 'geral' || tipo === 'proprio') {
    const flags = aplicarCondIptu(sel);
    itemCond(flags); itemIptu(flags); itemVariaveis('manutenção, móveis, reparos pontuais');
    if (tipo === 'geral') {
      // no Geral, cada cota fixa entra rateada pelo grupo do seu proprietário
      const donos = [...new Set(unidades.map(u => u.proprietario_id).filter(Boolean))];
      for (const id of donos) { const a = acordoDe(acordos, id); if (a && a.tipo === 'cota_fixa') itemCota(id, a, (unidades.find(u => u.proprietario_id === id).proprietarios || {}).nome); }
    }
    itemOutras();
  } else if (tipo === 'cota_fixa') {
    const flags = aplicarCondIptu(sel);
    itemCond(flags); itemCota(prop, acordo, null); itemIptu(flags); itemVariaveis('manutenção, móveis, reparos pontuais'); itemOutras();
  } else {
    // percentual / sem acordo: SEM condomínio nem IPTU (o proprietário paga direto)
    itemVariaveis('manutenção, móveis, reparos pontuais nos flats do proprietário'); itemOutras();
    if (tipo === 'percentual') {
      let taxa = 0;
      for (const f of sel) { const t = num(receitaPorFlat ? receitaPorFlat[f.id] : 0) * (num(acordo.valor) / 100); add(f.id, 'compartilhado', t); taxa += t; }
      itens.push({ label: 'Taxa de Administração', valor: taxa, sub: `${num(acordo.valor)}% × receita do período`, status: 'lancado' });
    } else {
      itens.push({ label: 'Taxa de Administração', valor: 0, sub: 'sem acordo cadastrado — defina na aba Acordos', status: 'zero' });
    }
  }
  for (const p of Object.values(porFlat)) p.total = p.condominio + p.iptu + p.variaveis + p.avulsos + p.compartilhado;
  return { total: itens.reduce((s, i) => s + i.valor, 0), itens, prop, tipo, porFlat };
}

// ── `_renderResumoRelatorioHtml` (23568), a conta: o resumo com o custo
// operacional unificado por flat e a rentabilidade líquida = receita − custo
export function resumoCompleto(dados, flatIds, de, ate, moeda = moedaPadrao) {
  const r = resumoFlats(dados, flatIds, de, ate, moeda);
  const donos = [...new Set(r.linhas.map(x => x.proprietarioId || SEM_PROPRIETARIO))];
  const prop = donos.length === 1 ? donos[0] : GERAL;
  const receitaPorFlat = {}; for (const x of r.linhas) receitaPorFlat[x.flatId] = x.receita || 0;
  const custo = custoOperacional(dados, prop, flatIds, de, ate, receitaPorFlat, moeda);
  for (const x of r.linhas) { const c = custo.porFlat[x.flatId]; x.custoOpDetalhe = c || null; x.custoOperacional = c ? c.total : 0; x.rentabilidadeLiquida = (x.receita || 0) - x.custoOperacional; }
  r.totalCustoOperacional = custo.total;
  r.rentabilidadeLiquida = (r.totalReceita || 0) - custo.total;
  const nomes = [...new Set(r.linhas.map(x => x.proprietario))];
  r.propRotulo = nomes.length === 1 ? nomes[0] : (nomes.length > 1 ? nomes.join(' + ') : '—');
  r.flatsNums = r.linhas.map(x => x.numero).join(', ');
  r.custo = custo; r.prop = prop;
  return r;
}
// a nota "Como é composto" do modal de custo (23744), pelo tipo do acordo
export function comoEComposto(custo) {
  if (custo.tipo === 'proprio') return 'Condomínio + IPTU + Despesas Variáveis. Funcionários, loja e frigobar NÃO entram no flat — são da operação inteira e aparecem no Financeiro → Resultado da Holding.';
  if (custo.tipo === 'cota_fixa') return 'Condomínio + cota fixa do acordo (rateada pelo grupo) + IPTU + Despesas Variáveis.';
  if (custo.tipo === 'geral') return 'Condomínio + IPTU + variáveis + cotas fixas rateadas + avulsos. Funcionários, loja e frigobar NÃO entram — são da operação inteira e aparecem no Financeiro → Resultado da Holding.';
  if (custo.tipo === 'percentual') return 'Despesas Variáveis dos seus flats + Taxa de Administração (% do acordo × receita). Sem condomínio nem IPTU (proprietário paga direto).';
  return 'Despesas Variáveis dos seus flats. Sem acordo da imobiliária cadastrado: sem taxa.';
}
// os 5 cards de KPI (23666)
export function cardsDoResumo(r, moeda) {
  const n = r.linhas.length;
  return [
    { chave: 'ocupacao', titulo: 'TAXA DE OCUPAÇÃO', valor: r.ocupacao.toFixed(1) + '%', grande: true, sub: `${r.totalNoitesOcup} / ${r.totalNoitesDisp} noites`, bg: '#ecfeff', borda: '#67e8f9', cor: '#0e7490', ic: 'ic-graficos' },
    { chave: 'receita', titulo: 'RECEITA DE DIÁRIAS', valor: moeda(r.totalReceita), sub: `${n} flat${n > 1 ? 's' : ''}`, bg: '#f0fdf4', borda: '#86efac', cor: '#059669', ic: 'ic-money-bag' },
    { chave: 'diaria', titulo: 'DIÁRIA MÉDIA', valor: moeda(r.diariaMedia || 0), sub: 'por noite ocupada', bg: '#faf5ff', borda: '#d8b4fe', cor: '#7c3aed', ic: 'ic-financeiro' },
    { chave: 'custo', titulo: 'CUSTO OPERACIONAL', valor: moeda(r.totalCustoOperacional), sub: `${r.custo.itens.length} item(s) · clique p/ detalhar`, bg: '#fff7ed', borda: '#fdba74', cor: '#c2410c', ic: 'ic-bank', clicavel: true },
    { chave: 'liquido', titulo: 'RENTABILIDADE LÍQ.', valor: moeda(r.rentabilidadeLiquida), corValor: r.rentabilidadeLiquida >= 0 ? '#059669' : '#dc2626', sub: 'diárias − custo operacional', bg: '#eff6ff', borda: '#93c5fd', cor: '#1d4ed8', ic: 'ic-graficos' },
  ];
}
// `_renderRelatorioChart` (24031): barras, ocupação (%) à esquerda × rentabilidade (R$) à direita
export function configResumo(r) {
  const fmtR = v => 'R$ ' + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  return {
    type: 'bar',
    data: { labels: r.linhas.map(x => 'Flat ' + x.numero), datasets: [
      { label: 'Ocupação (%)', data: r.linhas.map(x => Number((r.diasPeriodo > 0 ? (x.noites / r.diasPeriodo) * 100 : 0).toFixed(1))), backgroundColor: '#06b6d4cc', borderColor: '#0891b2', borderWidth: 1, yAxisID: 'y', borderRadius: 4 },
      { label: 'Rentabilidade líq. (R$)', data: r.linhas.map(x => Number((x.rentabilidadeLiquida || 0).toFixed(2))), backgroundColor: '#3b82f6cc', borderColor: '#1d4ed8', borderWidth: 1, yAxisID: 'y1', borderRadius: 4 }] },
    options: { responsive: true, maintainAspectRatio: false, animation: false,
      plugins: { legend: { position: 'top', labels: { font: { size: 11 } } }, tooltip: { callbacks: { label: c => (c.dataset.yAxisID === 'y' ? `${c.dataset.label}: ${c.parsed.y.toFixed(1)}%` : `${c.dataset.label}: ${fmtR(c.parsed.y)}`) } } },
      scales: { x: { grid: { display: false }, ticks: { font: { size: 10 } } },
        y: { type: 'linear', position: 'left', beginAtZero: true, max: 100, title: { display: true, text: 'Ocupação (%)', font: { size: 10 } }, ticks: { font: { size: 10 }, callback: v => v + '%' } },
        y1: { type: 'linear', position: 'right', beginAtZero: true, title: { display: true, text: 'Rentabilidade líq. (R$)', font: { size: 10 } }, ticks: { font: { size: 10 }, callback: v => (v >= 1000 ? 'R$' + (v / 1000).toFixed(0) + 'k' : 'R$' + v) }, grid: { display: false } } } },
  };
}

// ── `gerarRankingFlats` (22819): TODOS os flats, por receita das reservas ─
export function ranking(dados, de, ate, moeda = moedaPadrao) {
  const ids = (dados.unidades || []).map(u => u.id);
  const r = resumoFlats(dados, ids, de, ate, moeda);
  return { resumo: r, ranked: r.linhas.slice().sort((a, b) => (b.receitaDiarias || 0) - (a.receitaDiarias || 0)) };
}
export const MEDALHAS = Object.freeze(['🥇', '🥈', '🥉']);
export const PODIO = Object.freeze([{ bg: '#fffbeb', borda: '#f59e0b' }, { bg: '#f8fafc', borda: '#cbd5e1' }, { bg: '#fef3c7', borda: '#fbbf24' }]);
export const COLUNAS_RANKING = Object.freeze(['Pos.', 'Flat', 'Proprietário', 'Ocupação', 'Noites', 'Diária Média', 'Receita Reservas']);
export function linhasRanking(ranked, moeda) {
  return ranked.map((x, i) => [`${i + 1}º`, 'Flat ' + x.numero, x.proprietario, x.ocupacao.toFixed(1) + '%', String(x.noites), x.diariaMedia > 0 ? moeda(x.diariaMedia) : '—', moeda(x.receitaDiarias || 0)]);
}
export const COLUNAS_RANKING_EXCEL = Object.freeze(['Pos.', 'Flat', 'Proprietário', 'Ocupação (%)', 'Noites', 'Diária Média (R$)', 'Receita Reservas (R$)']);
export function linhasRankingExcel(ranked) {
  return ranked.map((x, i) => [i + 1, 'Flat ' + x.numero, x.proprietario, Number(x.ocupacao.toFixed(2)), x.noites, Number((x.diariaMedia || 0).toFixed(2)), Number((x.receitaDiarias || 0).toFixed(2))]);
}
// `_renderRankingFlatsChart` (22904)
export function configRanking(ranked) {
  return {
    type: 'bar',
    data: { labels: ranked.map(x => 'Flat ' + x.numero), datasets: [{ label: 'Receita Reservas (R$)', data: ranked.map(x => Number((x.receitaDiarias || 0).toFixed(2))), backgroundColor: '#f59e0bcc', borderColor: '#b45309', borderWidth: 1, borderRadius: 4 }] },
    options: { responsive: true, maintainAspectRatio: false, animation: false,
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: c => 'R$ ' + c.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) } } },
      scales: { x: { grid: { display: false }, ticks: { font: { size: 10 }, autoSkip: false, maxRotation: 60, minRotation: 30 } },
        y: { beginAtZero: true, ticks: { font: { size: 10 }, callback: v => (Math.abs(v) >= 1000 ? 'R$' + (v / 1000).toFixed(0) + 'k' : 'R$' + v) } } } },
  };
}

// ── o seletor de flats por proprietário (`_abrirFlatPickerRelatorio` 23466) ─
// grupos na ordem dos proprietários; a cor pelo acordo (próprio azul, cota
// fixa verde-escuro, o resto roxo — as 3 cores do MdM 23487)
export function gruposDoSeletor(unidades, acordos) {
  const grupos = new Map();
  for (const u of (unidades || []).slice().sort(porNumero)) {
    const k = u.proprietario_id || SEM_PROPRIETARIO;
    if (!grupos.has(k)) grupos.set(k, { id: k, nome: (u.proprietarios && u.proprietarios.nome) || 'Sem proprietário', flats: [] });
    grupos.get(k).flats.push(u);
  }
  return [...grupos.values()].map(g => {
    const a = g.id === SEM_PROPRIETARIO ? null : acordoDe(acordos, g.id);
    return { ...g, cor: a && a.tipo === 'proprio' ? '#1B3A7A' : a && a.tipo === 'cota_fixa' ? '#0f766e' : '#6b21a8' };
  }).sort((x, y) => x.nome.localeCompare(y.nome, 'pt-BR'));
}

// os nomes dos arquivos (23004 / 24980)
export function nomeDoArquivo(tipo, de, ate) {
  const base = { 'rel-reservas': 'Relatorio_Reservas', 'ranking-flats': 'Ranking_Flats', condominio: 'Relatorio_Condominio', funcionaria: 'Relatorio_Despesa_Funcionaria' }[tipo] || 'Relatorio';
  return `${base}_${de}_${ate}`;
}
